/**
 * App-wide: any truncated text — MUI chip labels, cells cut with an ellipsis
 * — shows its full text as a native tooltip on hover. One document-level
 * listener instead of wrapping every chip/cell in the app.
 *
 * Elements that already carry a tooltip (their own `title`, or an MUI
 * Tooltip, which marks its child with `aria-label`) are left alone. Titles set
 * here are tagged so they follow the text and drop once it fits again.
 */
const AUTO = 'data-overflow-title';

const isTruncated = (el: HTMLElement) => el.scrollWidth > el.clientWidth + 1;

const hasOwnTooltip = (el: HTMLElement) =>
	(el.hasAttribute('title') && !el.hasAttribute(AUTO)) ||
	el.hasAttribute('aria-label') ||
	Boolean(el.parentElement?.hasAttribute('aria-label'));

const onMouseOver = (event: MouseEvent) => {
	const target = event.target;
	if (!(target instanceof HTMLElement)) return;
	const el = (target.closest('.MuiChip-label') as HTMLElement | null) ?? target;
	// Text leaves only. A container that overflows horizontally (a scrollable
	// table wrapper — or <html> itself on a page with any sideways overflow)
	// passes the isTruncated test and would get its ENTIRE textContent as a
	// tooltip; for <html> that includes the <head>'s <style> sheets, shown as
	// a wall of raw CSS on hovering empty space.
	if (el === document.documentElement || el === document.body) return;
	if (el.childElementCount > 0) return;
	if (hasOwnTooltip(el)) return;
	const text = el.textContent?.trim();
	if (text && text.length <= 300 && isTruncated(el)) {
		if (el.getAttribute('title') !== text) el.setAttribute('title', text);
		el.setAttribute(AUTO, '');
	} else if (el.hasAttribute(AUTO)) {
		el.removeAttribute('title');
		el.removeAttribute(AUTO);
	}
};

export const installOverflowTitles = () => {
	document.addEventListener('mouseover', onMouseOver);
	return () => document.removeEventListener('mouseover', onMouseOver);
};

export default installOverflowTitles;
